import { seedNextSet, withEntry } from './loggerDraftHelpers';
import type { DraftEntry, LoggerDraft, WeightUnit } from './loggerDraftTypes';
import { resolveRowIndex } from './loggerDraftTypes';
import { loggerGroupReducer } from './loggerGroupReducer';

// #356: «More time», confirmed. One action, so the sheet's whole list lands
// in one step. Rows by id, never by index: the sheet builds the list across
// an await, and an index is only true for the render that made it (LG-43).
// New sets start from the set before (seedNextSet) and are to do.
// Delegated by loggerNestedReducer, like the calibration and session clusters.

export type MoreTimeStep =
  /** One more set on the row. */
  | { add: 'set'; entryId: string }
  /** One more round on every member of the superset. */
  | { add: 'round'; groupId: string }
  /** `reps` more on each set still to do. */
  | { add: 'reps'; entryId: string; reps: number }
  /** The row that comes back, built by the start chain. */
  | { add: 'entry'; entry: DraftEntry };

export type MoreTimeAction = {
  type: 'add_more_time';
  steps: readonly MoreTimeStep[];
  defaultUnit: WeightUnit;
};

/** A row in its MAX calibration (#353): its sets are the ramp's, 5 reps each
 *  (CALIBRATION_REPS), so «+1 set» and «+reps» leave it alone. */
export const inCalibration = (e: Pick<DraftEntry, 'calibration'>): boolean => e.calibration === 'accepted';

const withRow = (
  state: LoggerDraft,
  entryId: string,
  fn: (entry: DraftEntry) => DraftEntry,
): LoggerDraft => withEntry(state, resolveRowIndex(state.entries, entryId), fn);

const moreReps = (reps: string, more: number): string => {
  const n = Number(reps);
  return reps.trim() !== '' && Number.isFinite(n) ? String(n + more) : reps;
};

const applyStep = (
  state: LoggerDraft,
  step: MoreTimeStep,
  unit: WeightUnit,
): LoggerDraft => {
  switch (step.add) {
    case 'set':
      return withRow(state, step.entryId, (e) => (inCalibration(e) ? e : { ...e, sets: [...e.sets, seedNextSet(e, unit)] }));
    case 'round':
      // A member in its MAX calibration: the round is not added (as «+1 set»).
      if (state.entries.some((e) => e.group?.id === step.groupId && inCalibration(e))) return state;
      return loggerGroupReducer(state, { type: 'add_round_to_group', groupId: step.groupId, defaultUnit: unit });
    case 'reps':
      return withRow(state, step.entryId, (e) =>
        inCalibration(e)
          ? e
          : { ...e, sets: e.sets.map((s) => (s.completedAt === null ? { ...s, reps: moreReps(s.reps, step.reps) } : s)) },
      );
    case 'entry':
      return { ...state, entries: [...state.entries, step.entry] };
  }
};

export function moreTimeReducer(state: LoggerDraft, action: MoreTimeAction): LoggerDraft {
  return action.steps.reduce((s, step) => applyStep(s, step, action.defaultUnit), state);
}
